import React, { useState, useEffect } from 'react';
import { PageSection, SiteData, YachtItem, YachtMarina } from '../../../types';
import { dbService } from '../../../services/firebase';
import { Search, Calendar, Users, Anchor, MapPin, ArrowRight, RotateCcw, ShieldCheck, Sparkles, Plus, Eye } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor, getOptimalMutedTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
  onSelectYacht?: (yacht: YachtItem) => void;
}

export const YachtFilterSection: React.FC<SectionProps> = ({
  section,
  siteData,
  onNavigate,
  onSelectYacht,
}) => {
  const content = section.content || {};
  const style = section.style || {};

  const title = content.title || 'Mavi Tur & Yat Arama';
  const subtitle = content.subtitle || 'Tarih, liman, kişi sayısı ve tekne tipine göre filomuzda arama yapın';

  const [fleet, setFleet] = useState<YachtItem[]>([]);
  const [marinas, setMarinas] = useState<YachtMarina[]>([]);
  const [isLoadingFleet, setIsLoadingFleet] = useState(true);

  // Search form fields
  const [selectedPort, setSelectedPort] = useState<string>('ALL');
  const [selectedType, setSelectedType] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [guestCount, setGuestCount] = useState<number>(0);
  const [minCabins, setMinCabins] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);

  // Search state & results
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchResults, setSearchResults] = useState<YachtItem[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Field configurations
  const configuredFields = content.fields && Array.isArray(content.fields) && content.fields.length > 0
    ? content.fields
    : [
        { id: 'port', label: 'Çıkış Limanı', placeholder: 'Tüm Limanlar', visible: true, required: false },
        { id: 'dates', label: 'Tur Tarihleri', placeholder: 'Tarih Seçin', visible: true, required: false },
        { id: 'yachtType', label: 'Yat Tipi', placeholder: 'Tüm Tipler', visible: true, required: false },
        { id: 'guests', label: 'Kişi Sayısı', placeholder: 'Kişi sayısı', visible: true, required: false },
      ];

  const searchButtonLabel = content.searchButtonText || 'Yatları Listele';
  const resetButtonLabel = content.resetButtonText || 'Aramayı Temizle';

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        setIsLoadingFleet(true);
        const [yachtsData, marinasData] = await Promise.all([
          dbService.getYachts(siteData.id),
          dbService.getMarinas ? dbService.getMarinas(siteData.id) : Promise.resolve([]),
        ]);
        if (isMounted) {
          setFleet(yachtsData || []);
          setMarinas(marinasData || []);
        }
      } catch (err) {
        console.warn('Yacht filter section data loading error:', err);
        if (isMounted) {
          setFleet([]);
          setMarinas([]);
        }
      } finally {
        if (isMounted) setIsLoadingFleet(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [siteData.id]);

  // Derive actual ports from real yachts and marinas (NO fake defaults)
  const availablePorts = Array.from(
    new Set(
      fleet
        .map((y) => y.portLocation || y.basePort || y.location)
        .concat(marinas.map((m) => m.name || m.location))
        .filter((p): p is string => Boolean(p && p.trim()))
    )
  );

  // Derive actual yacht types from real yachts (NO fake defaults)
  const availableTypes = Array.from(
    new Set(fleet.map((y) => y.type).filter((t): t is string => Boolean(t && t.trim())))
  );

  const handleReset = () => {
    setSelectedPort('ALL');
    setSelectedType('ALL');
    setStartDate('');
    setEndDate('');
    setGuestCount(0);
    setMinCabins(0);
    setMaxPrice(0);
    setHasSearched(false);
    setSearchResults([]);
    setSearchError(null);
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSearchError(null);

    if (startDate && endDate && startDate > endDate) {
      setSearchError('Bitiş tarihi başlangıç tarihinden önce olamaz.');
      return;
    }

    // Check required fields
    for (const field of configuredFields) {
      if (field.visible && field.required) {
        if (field.id === 'port' && selectedPort === 'ALL') {
          setSearchError(`${field.label || 'Çıkış Limanı'} seçimi zorunludur.`);
          return;
        }
        if (field.id === 'dates' && (!startDate || !endDate)) {
          setSearchError(`${field.label || 'Tur Tarihleri'} seçimi zorunludur.`);
          return;
        }
        if (field.id === 'yachtType' && selectedType === 'ALL') {
          setSearchError(`${field.label || 'Yat Tipi'} seçimi zorunludur.`);
          return;
        }
        if (field.id === 'guests' && guestCount <= 0) {
          setSearchError(`${field.label || 'Kişi Sayısı'} belirtilmesi zorunludur.`);
          return;
        }
        if (field.id === 'cabins' && minCabins <= 0) {
          setSearchError(`${field.label || 'Kabin Sayısı'} belirtilmesi zorunludur.`);
          return;
        }
      }
    }

    setIsSearching(true);
    try {
      // 1. Initial filter by port, type, capacity, cabins, price
      let filtered = fleet.filter((yacht) => {
        if (yacht.status === 'MAINTENANCE') return false;

        // Port filter
        if (selectedPort !== 'ALL') {
          const yachtPort = (yacht.portLocation || yacht.basePort || yacht.location || '').toLowerCase();
          if (!yachtPort.includes(selectedPort.toLowerCase())) return false;
        }

        // Type filter
        if (selectedType !== 'ALL' && yacht.type !== selectedType) {
          return false;
        }

        // Guests capacity filter
        if (guestCount > 0 && (yacht.capacity || 0) < guestCount) {
          return false;
        }

        // Cabins filter
        if (minCabins > 0 && (yacht.cabins || 0) < minCabins) {
          return false;
        }

        // Max price filter
        if (maxPrice > 0 && (yacht.pricePerDay || 0) > maxPrice) {
          return false;
        }

        return true;
      });

      // 2. Date availability check if dates are specified
      if (startDate && endDate) {
        const availableYachts: YachtItem[] = [];
        for (const yacht of filtered) {
          const check = await dbService.checkYachtAvailability(siteData.id, yacht.id, startDate, endDate);
          if (check.available) {
            availableYachts.push(yacht);
          }
        }
        filtered = availableYachts;
      }

      setSearchResults(filtered);
      setHasSearched(true);
    } catch (err: any) {
      console.error('Search error:', err);
      setSearchError(err?.message || 'Arama sırasında bir hata oluştu.');
    } finally {
      setIsSearching(false);
    }
  };

  const bgColor = style.backgroundColor || '#041f3d';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const mutedTextColor = getOptimalMutedTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#0284C7';

  // Today for min date
  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <section
      id="yacht-search-section"
      className={`py-16 px-4 sm:px-6 lg:px-8 border-y ${
        isDark ? 'border-white/10' : 'border-slate-200'
      }`}
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-teal-500/10 text-teal-400">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Müsaitlik ve Rezervasyon</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-xs sm:text-sm leading-relaxed" style={{ color: mutedTextColor }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Search Filter Bar */}
        <form
          onSubmit={handleSearch}
          className={`p-4 sm:p-6 rounded-3xl border shadow-2xl transition-all ${
            isDark
              ? 'bg-slate-900/90 backdrop-blur-xl border-white/15'
              : 'bg-white border-slate-200 text-slate-800'
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3.5 items-end">
            {configuredFields
              .filter((f) => f.visible !== false)
              .map((field) => {
                if (field.id === 'port') {
                  return (
                    <div key="port" className="space-y-1.5">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <MapPin className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Çıkış Limanı'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <select
                        value={selectedPort}
                        onChange={(e) => setSelectedPort(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer transition ${
                          isDark
                            ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                        }`}
                      >
                        <option value="ALL">{field.placeholder || 'Tüm Limanlar'} {availablePorts.length === 0 ? '(Belirtilmemiş)' : ''}</option>
                        {availablePorts.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                if (field.id === 'dates') {
                  return (
                    <div key="dates" className="space-y-1.5 sm:col-span-2">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <Calendar className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Tur Tarihleri (Alış - İade)'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          min={todayStr}
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          placeholder="Başlangıç"
                          className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer transition ${
                            isDark
                              ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                              : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                          }`}
                        />
                        <input
                          type="date"
                          min={startDate || todayStr}
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          placeholder="Bitiş"
                          className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer transition ${
                            isDark
                              ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                              : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                          }`}
                        />
                      </div>
                    </div>
                  );
                }

                if (field.id === 'yachtType') {
                  return (
                    <div key="yachtType" className="space-y-1.5">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <Anchor className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Yat Tipi'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer ${
                          isDark
                            ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                        }`}
                      >
                        <option value="ALL">{field.placeholder || 'Tüm Tipler'}</option>
                        {availableTypes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                if (field.id === 'guests') {
                  return (
                    <div key="guests" className="space-y-1.5">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <Users className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Kişi Sayısı'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <select
                        value={guestCount}
                        onChange={(e) => setGuestCount(Number(e.target.value))}
                        className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer ${
                          isDark
                            ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                        }`}
                      >
                        <option value={0}>{field.placeholder || 'Tüm Kapasiteler'}</option>
                        <option value={2}>2+ Kişi</option>
                        <option value={4}>4+ Kişi</option>
                        <option value={6}>6+ Kişi</option>
                        <option value={8}>8+ Kişi</option>
                        <option value={10}>10+ Kişi</option>
                        <option value={12}>12+ Kişi</option>
                      </select>
                    </div>
                  );
                }

                if (field.id === 'cabins') {
                  return (
                    <div key="cabins" className="space-y-1.5">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <Anchor className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Min. Kabin'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <select
                        value={minCabins}
                        onChange={(e) => setMinCabins(Number(e.target.value))}
                        className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer ${
                          isDark
                            ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                        }`}
                      >
                        <option value={0}>{field.placeholder || 'Tüm Kabinler'}</option>
                        <option value={2}>2+ Kabin</option>
                        <option value={3}>3+ Kabin</option>
                        <option value={4}>4+ Kabin</option>
                        <option value={5}>5+ Kabin</option>
                        <option value={6}>6+ Kabin</option>
                      </select>
                    </div>
                  );
                }

                if (field.id === 'price') {
                  return (
                    <div key="price" className="space-y-1.5">
                      <label
                        className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${
                          isDark ? 'text-slate-300' : 'text-slate-700'
                        }`}
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-teal-500" />
                        <span>{field.label || 'Maks. Günlük Fiyat'}</span>
                        {field.required && <span className="text-red-400">*</span>}
                      </label>
                      <select
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(Number(e.target.value))}
                        className={`w-full px-3 py-2.5 rounded-xl text-xs font-semibold border outline-none cursor-pointer ${
                          isDark
                            ? 'bg-slate-800 border-slate-700 text-white focus:border-teal-500'
                            : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-teal-500'
                        }`}
                      >
                        <option value={0}>{field.placeholder || 'Limitsiz Bütçe'}</option>
                        <option value={2000}>2.000 € altı</option>
                        <option value={3500}>3.500 € altı</option>
                        <option value={5000}>5.000 € altı</option>
                        <option value={7500}>7.500 € altı</option>
                        <option value={10000}>10.000 € altı</option>
                      </select>
                    </div>
                  );
                }

                return null;
              })}

            {/* Submit Button */}
            <div className="space-y-1.5">
              <button
                type="submit"
                disabled={isSearching}
                className="w-full py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold shadow-lg hover:shadow-teal-500/25 transition-all flex items-center justify-center gap-2 text-white bg-teal-600 hover:bg-teal-500 active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                <span>{isSearching ? 'Aranıyor...' : searchButtonLabel}</span>
              </button>
            </div>
          </div>

          {searchError && (
            <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold">
              {searchError}
            </div>
          )}
        </form>

        {/* Search Results Display Area */}
        {hasSearched && (
          <div className="space-y-6 pt-4">
            <div className="flex items-center justify-between flex-wrap gap-3 border-b pb-3 border-white/10">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-teal-400" />
                <h4 className="text-base font-bold">
                  Arama Sonuçları ({searchResults.length} Müsait Yat)
                </h4>
                {startDate && endDate && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300 font-medium">
                    {startDate} - {endDate}
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="text-xs font-semibold flex items-center gap-1.5 opacity-80 hover:opacity-100 hover:underline text-teal-400 cursor-pointer"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>{resetButtonLabel}</span>
              </button>
            </div>

            {searchResults.length === 0 ? (
              <div
                className={`p-10 rounded-3xl border text-center space-y-3 ${
                  isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 text-slate-700'
                }`}
              >
                <Anchor className="w-10 h-10 mx-auto text-slate-400" />
                <div className="space-y-1">
                  <h5 className="text-sm font-bold">Aramanıza Uygun Yat Bulunamadı</h5>
                  <p className="text-xs text-slate-400 max-w-md mx-auto">
                    {fleet.length === 0
                      ? 'Filonuzda henüz kayıtlı bir tekne bulunmuyor. Yönetim panelinden ilk teknenizi ekleyebilirsiniz.'
                      : 'Seçtiğiniz tarih, kapasite veya liman kriterlerine uygun müsait bir tekne bulunamadı. Lütfen tarihleri veya filtreleri değiştirerek tekrar deneyin.'}
                  </p>
                </div>
                {fleet.length === 0 && onNavigate && (
                  <button
                    type="button"
                    onClick={() => onNavigate('#yacht-fleet')}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-teal-600 text-white text-xs font-bold"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Yat Yönetimine Git</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((yacht) => (
                  <div
                    key={yacht.id}
                    className={`rounded-2xl overflow-hidden border shadow-lg flex flex-col justify-between transition hover:-translate-y-1 ${
                      isDark
                        ? 'bg-slate-900 border-white/10 text-white'
                        : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  >
                    <div className="relative aspect-16/10 bg-slate-950 overflow-hidden">
                      <img
                        src={yacht.coverImage || 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?auto=format&fit=crop&w=800&q=80'}
                        alt={yacht.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3 bg-slate-950/80 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                        <Anchor className="w-3 h-3 text-teal-400" />
                        <span>{yacht.type}</span>
                      </div>
                      {(yacht.portLocation || yacht.basePort) && (
                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-md text-slate-900 text-[10px] font-bold px-2.5 py-1 rounded-full">
                          {yacht.portLocation || yacht.basePort}
                        </div>
                      )}
                    </div>

                    <div className="p-5 space-y-3 flex-1 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        <h5 className="font-bold text-base">{yacht.name}</h5>
                        <p className={`text-xs line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {yacht.description}
                        </p>
                        <div className={`grid grid-cols-3 gap-1.5 pt-2 text-center text-[10px] font-bold ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                          <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                            {yacht.lengthMeters || yacht.length || '-'}m
                          </div>
                          <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                            {yacht.capacity} Kişi
                          </div>
                          <div className={`p-1.5 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                            {yacht.cabins} Kabin
                          </div>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-semibold">Günlük</div>
                          <div className="text-base font-black">
                            {yacht.currency === 'USD' ? '$' : yacht.currency === 'TRY' ? '₺' : '€'}
                            {(yacht.pricePerDay || 0).toLocaleString()}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (onSelectYacht) onSelectYacht(yacht);
                              else if (onNavigate) onNavigate(`/yat/${yacht.id}`);
                            }}
                            className={`px-3 py-2 rounded-xl text-xs font-semibold border transition flex items-center gap-1 cursor-pointer ${
                              isDark
                                ? 'bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-200'
                                : 'bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-800'
                            }`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>İncele</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (onSelectYacht) onSelectYacht(yacht);
                              if (onNavigate) onNavigate('/rezervasyon');
                            }}
                            className="px-3.5 py-2 rounded-xl text-xs font-bold shadow transition flex items-center gap-1 text-white bg-teal-600 hover:bg-teal-500 cursor-pointer"
                          >
                            <span>Rezervasyon Talebi</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
